import * as THREE from 'three/webgpu'
import {
  FeatureFace,
  FeatureLine,
  FeaturePoint,
  FeatureResultMesh,
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
} from '../../mesh/feature'
import { resolvePrimaryEntityHit } from '../../manager/selection/SelectionHitArbitration'
import type { EntitySelectionMode } from '../../manager/selection/EntitySelectionMode'
import { setRaycasterFirstHitOnly } from '../../raycast-acceleration/setRaycasterFirstHitOnly'
import { markImportedTopologyProxy } from './imported-topology-proxy-metadata'
import { ImportedModelTopologySnapshotPicker } from './ImportedModelTopologySnapshotPicker'
import {
  ensureImportedModelTopologyPickHost,
  isImportedModelTopologyHost,
  resolveImportedModelBodyTopologyRecord,
} from '../topology/imported-model-topology-runtime'
import { resolveImportedModelTopologyRepository } from '../topology/ImportedModelTopologyRepository'

/**
 * 导入拓扑拾取输入。
 */
export interface ImportedModelTopologyPickInput extends ImportedModelTopologySnapshotPickContext {
  /** 第一阶段解析出的导入模型宿主对象。 */
  hostObject: THREE.Object3D
  /** 第一阶段可见 Mesh 射线命中。 */
  rawHit: PuppyCAD.ImportedModelResolvedRaycastHit
  /** 当前实体选择模式。 */
  selectionMode: EntitySelectionMode
  /** 是否允许被前景面遮挡的拓扑参与精筛。 */
  allowOccludedSelection: boolean
}

/**
 * 判断当前命中对象是否已经是 imported 拓扑对象。
 *
 * @param object 目标对象。
 * @returns 是否已经进入 imported 拓扑选择链。
 */
export function isImportedTopologySelectionObject(object: THREE.Object3D | null | undefined): boolean {
  return resolveImportedTopologyHost(object) !== null
}

/**
 * 解析导入拓扑对象所属的 pick host。
 *
 * @param object 目标对象。
 * @returns 对应的 imported pick host；不存在时返回 `null`。
 */
export function resolveImportedTopologyHost(
  object: THREE.Object3D | null | undefined,
): FeatureResultMesh | null {
  if (!object) {
    return null
  }

  if (isImportedModelTopologyHost(object)) {
    return object as unknown as FeatureResultMesh
  }

  if (isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object)) {
    const owner = object.getOwningResultMesh()
    return isImportedModelTopologyHost(owner) ? owner as unknown as FeatureResultMesh : null
  }

  let current: THREE.Object3D | null = object.parent
  while (current) {
    if (isImportedModelTopologyHost(current)) {
      return current as unknown as FeatureResultMesh
    }
    current = current.parent
  }

  return null
}

/**
 * 通过拓扑选择对象反查实际承载该拓扑的 imported visible source Mesh。
 *
 * 设计说明：
 * 1. `FeatureFace/FeatureLine/FeaturePoint` 是 pick host 的派生选择对象，不是组件变换根；
 * 2. 面必须按权威 faceIndex 解析多 Mesh Body 中的真实承载 Mesh，禁止固定取第一条记录；
 * 3. 线、点和 host 只用于解析 occurrence 时，可使用当前 Body 记录的 visible Mesh，
 *    因为同一 Body 的显示 Mesh 必须属于同一个组件 occurrence。
 *
 * @param object 当前拓扑选择对象或 pick host。
 * @returns 对应业务 source Mesh；缺少仓库或绑定时返回 null。
 */
export function resolveImportedTopologyVisibleSourceMesh(
  object: THREE.Object3D | null | undefined,
): THREE.Mesh | null {
  const topologyHost = resolveImportedTopologyHost(object)
  if (!topologyHost) {
    return null
  }
  const repository = resolveImportedModelTopologyRepository(topologyHost)
  if (!repository) {
    return null
  }
  if (isFeatureFace(object)) {
    return repository.resolveVisibleMeshForPickHostFace(topologyHost, object.faceIndex)
  }
  return repository.resolveBodyRecordFromPickHost(topologyHost)?.visibleMesh ?? null
}

/**
 * 判断已命中的对象是否与当前过滤模式兼容。
 *
 * @param object 已命中的对象。
 * @param selectionMode 当前过滤模式。
 * @returns 是否可直接复用。
 */
function isDirectFeatureHitCompatible(
  object: THREE.Object3D,
  selectionMode: EntitySelectionMode,
): boolean {
  if (selectionMode === 'object') {
    return isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object)
  }
  if (selectionMode === 'face') {
    return isFeatureFace(object)
  }
  if (selectionMode === 'edge') {
    return isFeatureLine(object)
  }
  if (selectionMode === 'vertex') {
    return isFeaturePoint(object)
  }
  if (selectionMode === 'face-edge') {
    return isFeatureFace(object) || isFeatureLine(object)
  }
  if (selectionMode === 'topology') {
    return isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object)
  }
  return false
}

/**
 * 将 pick host 的三角面命中映射为权威面快照候选。
 *
 * @param pickHost imported pick host。
 * @param meshHits pick host 的原始三角面命中集合。
 * @returns 按射线距离排序的面快照候选，不创建 `FeatureFace` 代理。
 */
function resolveImportedTopologyFaceCandidates(
  pickHost: FeatureResultMesh,
  meshHits: Array<THREE.Intersection<THREE.Object3D>>,
): ImportedModelTopologyFaceSnapshotCandidate[] {
  const faceHitsByFaceIndex = new Map<number, ImportedModelTopologyFaceSnapshotCandidate>()

  for (const meshHit of meshHits) {
    const face = typeof meshHit.faceIndex === 'number'
      ? pickHost.getTopologyFaceSnapshotByTriangleIndex(meshHit.faceIndex)
      : null
    if (!face) {
      continue
    }

    const existing = faceHitsByFaceIndex.get(face.index)
    if (!existing || meshHit.distance < existing.hit.distance) {
      faceHitsByFaceIndex.set(face.index, { face, hit: meshHit })
    }
  }

  return Array.from(faceHitsByFaceIndex.values())
    .sort((left, right) => left.hit.distance - right.hit.distance)
}

/**
 * 为纯快照顶点候选构造不带显示代理的仲裁命中。
 *
 * @param candidate 顶点候选。
 * @param hostObject 仲裁阶段使用的占位宿主对象。
 * @returns 可交给统一选择仲裁器的命中。
 */
function createVertexArbitrationHit(
  candidate: ImportedModelTopologyVertexSnapshotHit | null,
  hostObject: THREE.Object3D,
): THREE.Intersection<THREE.Object3D> | null {
  if (!candidate) {
    return null
  }

  return {
    distance: candidate.distance,
    point: candidate.point,
    object: hostObject,
    screenDistance: candidate.screenDistance,
    rayDistance: candidate.rayDistance,
  } as THREE.Intersection<THREE.Object3D>
}

/**
 * 为纯快照边候选构造不带显示代理的仲裁命中。
 *
 * @param candidate 边候选。
 * @param hostObject 仲裁阶段使用的占位宿主对象。
 * @returns 可交给统一选择仲裁器的命中。
 */
function createEdgeArbitrationHit(
  candidate: ImportedModelTopologyEdgeSnapshotHit | null,
  hostObject: THREE.Object3D,
): THREE.Intersection<THREE.Object3D> | null {
  if (!candidate) {
    return null
  }

  return {
    distance: candidate.distance,
    point: candidate.point,
    object: hostObject,
    screenDistance: candidate.screenDistance,
    visibleHalfWidth: candidate.visibleHalfWidth,
    segmentParameter: candidate.segmentParameter,
  } as THREE.Intersection<THREE.Object3D>
}

/**
 * 导入拓扑二阶段局部仲裁器。
 *
 * 设计说明：
 * 1. 第一阶段只允许 visible GLB mesh 参与命中，用于定位 Body 与显式绑定的拓扑面；
 * 2. 显式范围有效时，以该面为点线面仲裁依据；缺少映射才对 BRep pick host 二次射线回退；
 * 3. 允许遮挡选择时可补充背后面候选，但不得覆盖显式映射的前景面；
 * 4. 最终只把真正命中的 `FeatureLine / FeatureFace / FeaturePoint` 暴露给统一选择状态机。
 */
export class ImportedModelTopologyPickResolver {
  /** 直接消费权威拓扑快照的屏幕空间拾取器。 */
  private readonly topologySnapshotPicker = new ImportedModelTopologySnapshotPicker()

  /** 当前射线命中且允许参与二阶段精筛的面快照缓存。 */
  private readonly candidateFaces: TopologySnapshotFace[] = []

  private readonly topologyRaycaster = setRaycasterFirstHitOnly(new THREE.Raycaster())

  /**
   * 结束消费者持有期，归还所有派生缓存和快照订阅。
   *
   * 宿主可能仍归外部文档所有，因此这里只解除借用，不清空宿主拓扑或释放其资源。
   */
  public clear(): void {
    this.topologySnapshotPicker.clear()
    this.candidateFaces.length = 0
  }

  /**
   * 解析当前 imported 命中的最终点/线/面/体选择对象。
   *
   * @param input 当前拾取输入。
   * @returns 最终命中；无法解析时返回 `null`。
   */
  public resolve(input: ImportedModelTopologyPickInput): THREE.Intersection<THREE.Object3D> | null {
    try {
      return this.resolveHit(input)
    } finally {
      // 候选面只在同步仲裁期间借用；命中、未命中和物化异常都不能把旧文档快照留在服务中。
      this.candidateFaces.length = 0
    }
  }

  /**
   * 在当前查询借用期内完成映射、局部仲裁和最终代理物化。
   *
   * @param input 当前拾取输入。
   * @returns 最终命中；借用缓存由公开入口统一归还。
   */
  private resolveHit(input: ImportedModelTopologyPickInput): THREE.Intersection<THREE.Object3D> | null {
    if (isDirectFeatureHitCompatible(input.hostObject, input.selectionMode)) {
      return {
        ...input.rawHit,
        object: input.hostObject,
      }
    }

    const bodyRecord = resolveImportedModelBodyTopologyRecord(input.rawHit)
    if (!bodyRecord) {
      return null
    }

    const pickHost = ensureImportedModelTopologyPickHost(bodyRecord)
    pickHost.updateMatrixWorld(true)

    const mappedFace = typeof input.rawHit.faceIndex === 'number'
      ? bodyRecord.resolveTopologyFaceByTriangleIndex(input.rawHit.faceIndex)
      : null
    /**
     * 旧包可能给每个材质 Mesh 保存独立局部面编号，不能用编号相同冒充共享宿主中的同一个面。
     * 只有反查结果就是当前 host 持有的原始快照时才接受，否则保留既有 BRep 回退。
     */
    let primaryFace = mappedFace && pickHost.getTopologyFaceSnapshotByIndex(mappedFace.index) === mappedFace
      ? mappedFace
      : null
    let primaryFaceHit: THREE.Intersection<THREE.Object3D> | null = primaryFace ? input.rawHit : null
    this.candidateFaces.length = 0
    if (primaryFace) {
      this.candidateFaces.push(primaryFace)
    }

    if (!primaryFace || input.allowOccludedSelection) {
      this.topologyRaycaster.ray.copy(input.ray)
      const previousFirstHitOnly = Reflect.get(this.topologyRaycaster, 'firstHitOnly') === true
      setRaycasterFirstHitOnly(this.topologyRaycaster, !input.allowOccludedSelection)
      let meshHits: Array<THREE.Intersection<THREE.Object3D>> = []
      try {
        meshHits = this.topologyRaycaster.intersectObject(pickHost, false)
      } finally {
        setRaycasterFirstHitOnly(this.topologyRaycaster, previousFirstHitOnly)
      }
      const faceCandidates = resolveImportedTopologyFaceCandidates(pickHost, meshHits)
      if (!primaryFace) {
        const firstCandidate = faceCandidates[0]
        primaryFace = firstCandidate?.face ?? null
        primaryFaceHit = firstCandidate?.hit ?? null
        if (primaryFace) {
          this.candidateFaces.push(primaryFace)
        }
      }
      if (input.allowOccludedSelection) {
        for (const candidate of faceCandidates) {
          if (candidate.face !== primaryFace) {
            this.candidateFaces.push(candidate.face)
          }
        }
      }
    }
    if (!primaryFace || !primaryFaceHit) {
      return null
    }

    const shouldPickEdges = input.selectionMode === 'object'
      || input.selectionMode === 'edge'
      || input.selectionMode === 'face-edge'
      || input.selectionMode === 'topology'
    const shouldPickVertices = input.selectionMode === 'object'
      || input.selectionMode === 'vertex'
      || input.selectionMode === 'topology'

    /**
     * 实例组的同一 pick host 会在多个 GPU 槽之间移动；不可变 Unit 快照条目才是准确投影身份。
     * 普通 Mesh 没有实例条目，继续以 source Mesh 对象身份缓存。
     */
    const projectionIdentity = input.rawHit.importedModelInstanceEntry ?? input.rawHit.object

    const edgeCandidate = this.candidateFaces.length > 0 && shouldPickEdges
      ? this.topologySnapshotPicker.pickNearestEdge(
        pickHost,
        this.candidateFaces,
        input,
        projectionIdentity,
      )
      : null
    const vertexCandidate = this.candidateFaces.length > 0 && shouldPickVertices
      ? this.topologySnapshotPicker.pickNearestVertex(
        pickHost,
        this.candidateFaces,
        input,
        projectionIdentity,
      )
      : null

    /**
     * 统一仲裁只读取距离信息，因此使用 visible mesh 作为占位对象即可。只有胜出的候选才会在
     * 后续物化为真实 FeaturePoint / FeatureLine / FeatureFace，禁止候选阶段创建 Three.js 资源。
     */
    const lineHit = createEdgeArbitrationHit(edgeCandidate, input.hostObject)
    const pointHit = createVertexArbitrationHit(vertexCandidate, input.hostObject)
    const faceHit = {
      ...primaryFaceHit,
      object: input.hostObject,
    } as THREE.Intersection<FeatureFace>

    if (input.selectionMode === 'vertex') {
      return vertexCandidate && pointHit
        ? this.materializeVertexHit(pickHost, vertexCandidate, pointHit)
        : null
    }

    if (input.selectionMode === 'edge') {
      return edgeCandidate && lineHit
        ? this.materializeEdgeHit(pickHost, edgeCandidate, lineHit)
        : null
    }

    if (input.selectionMode === 'face') {
      return this.materializeFaceHit(pickHost, primaryFace.index, primaryFaceHit)
    }

    const winningHit = resolvePrimaryEntityHit({
      pointHit,
      lineHit,
      faceBoundaryLineHit: lineHit,
      faceHit,
      meshHit: null,
      selectionMode: input.selectionMode,
    })

    if (winningHit === pointHit && vertexCandidate) {
      return this.materializeVertexHit(pickHost, vertexCandidate, pointHit)
    }
    if (winningHit === lineHit && edgeCandidate) {
      return this.materializeEdgeHit(pickHost, edgeCandidate, lineHit)
    }
    if (winningHit === faceHit) {
      return this.materializeFaceHit(
        pickHost,
        primaryFace.index,
        primaryFaceHit,
      )
    }

    return null
  }

  /**
   * 把最终顶点候选物化为真实选择代理。
   *
   * @param host 当前拓扑宿主。
   * @param candidate 最终顶点快照命中。
   * @param arbitrationHit 已通过统一选择规则的命中数据。
   * @returns 真实顶点代理命中。
   */
  private materializeVertexHit(
    host: FeatureResultMesh,
    candidate: ImportedModelTopologyVertexSnapshotHit,
    arbitrationHit: THREE.Intersection<THREE.Object3D>,
  ): THREE.Intersection<THREE.Object3D> | null {
    const featurePoint = host.attachFeaturePointByIndex(candidate.vertexIndex)
    if (!(featurePoint instanceof FeaturePoint)) {
      return null
    }

    markImportedTopologyProxy(featurePoint)
    return {
      ...arbitrationHit,
      // 最终命中可能进入 selected 状态，必须脱离拾取器下一帧会复写的热路径向量缓存。
      point: arbitrationHit.point.clone(),
      object: featurePoint,
    }
  }

  /**
   * 把最终边候选物化为真实选择代理。
   *
   * @param host 当前拓扑宿主。
   * @param candidate 最终边快照命中。
   * @param arbitrationHit 已通过统一选择规则的命中数据。
   * @returns 真实边代理命中。
   */
  private materializeEdgeHit(
    host: FeatureResultMesh,
    candidate: ImportedModelTopologyEdgeSnapshotHit,
    arbitrationHit: THREE.Intersection<THREE.Object3D>,
  ): THREE.Intersection<THREE.Object3D> | null {
    const featureLine = host.attachFeatureLineByIndex(candidate.edgeIndex)
    if (!(featureLine instanceof FeatureLine)) {
      return null
    }

    markImportedTopologyProxy(featureLine)
    return {
      ...arbitrationHit,
      // 最终命中可能进入 selected 状态，必须脱离拾取器下一帧会复写的热路径向量缓存。
      point: arbitrationHit.point.clone(),
      object: featureLine,
    }
  }

  /**
   * 把最终面候选物化为真实选择代理。
   *
   * @param host 当前拓扑宿主。
   * @param faceIndex 最终面索引。
   * @param rawHit 显式映射采用 visible Mesh 命中，回退路径采用 pick host 三角命中。
   * @returns 真实面代理命中。
   */
  private materializeFaceHit(
    host: FeatureResultMesh,
    faceIndex: number,
    rawHit: THREE.Intersection<THREE.Object3D>,
  ): THREE.Intersection<THREE.Object3D> | null {
    const featureFace = host.attachFeatureFaceByIndex(faceIndex)
    if (!(featureFace instanceof FeatureFace)) {
      return null
    }

    markImportedTopologyProxy(featureFace)
    return {
      ...rawHit,
      // FeatureFace 自身持有权威拓扑面索引；显示 Mesh 的三角形编号不属于 host 几何，禁止透传冒用。
      ...(rawHit.object !== host ? { face: null, faceIndex: null } : {}),
      object: featureFace,
    }
  }
}
